/**
 * QuestionEditor — edits ONE question within a section (VER-109).
 *
 * Renders the question-type selector, the text field, a type-specific config
 * panel, and the reverse / scored / required toggles. Reorder + duplicate +
 * delete controls are supplied by the parent SectionEditor.
 */
import React from "react";
import {
    Box,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { ContentCopyOutlined, DeleteOutline } from "@mui/icons-material";
import { useFormikContext } from "formik";
import type { QuestionType, SurveyDefinition, SurveyQuestion } from "./survey.types";
import { NUMERIC_SINGLE_TYPES, effectiveOptions } from "./survey.types";
import { applyTypeChange } from "./builder.mock";
import { OptionsEditor } from "./OptionsEditor";
import { ReorderControls, ReorderControlsProps } from "./ReorderControls";
import { useFieldError } from "./useFieldError";

const TYPE_LABELS: Record<QuestionType, string> = {
    likert: "Rating grid (Likert)",
    single: "Single choice",
    multi: "Multiple choice",
    freeText: "Free text",
    vas: "Slider (VAS)",
    nps: "NPS 0–10",
};

export type QuestionEditorProps = {
    sectionIndex: number;
    questionIndex: number;
    question: SurveyQuestion;
    reorder: Omit<ReorderControlsProps, "label">;
    onDuplicate: () => void;
    onDelete: () => void;
    canDelete: boolean;
};

export const QuestionEditor = ({
    sectionIndex,
    questionIndex,
    question,
    reorder,
    onDuplicate,
    onDelete,
    canDelete,
}: QuestionEditorProps): JSX.Element => {
    const { values, handleChange, handleBlur, setFieldValue } = useFormikContext<SurveyDefinition>();
    const fieldError = useFieldError();
    const path = `sections.${sectionIndex}.questions.${questionIndex}`;

    const showReverse = NUMERIC_SINGLE_TYPES.includes(question.type);
    const inheritsScale =
        (question.type === "likert" || question.type === "single") &&
        (!question.options || question.options.length === 0);
    const inheritedCount = effectiveOptions(values, question).length;

    return (
        <Paper
            variant="outlined"
            sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: (theme) => `${theme.shape.borderRadius}px` }}
        >
            <Box display="flex" alignItems="flex-start" gap={1} mb={2}>
                <ReorderControls label={`question ${questionIndex + 1}`} {...reorder} />
                <Box flex={1}>
                    <TextField
                        fullWidth
                        size="small"
                        name={`${path}.text`}
                        label={`Question ${questionIndex + 1}`}
                        value={question.text}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(fieldError(`${path}.text`))}
                        helperText={fieldError(`${path}.text`)}
                    />
                </Box>
                <Tooltip title="Duplicate question">
                    <IconButton aria-label={`Duplicate question ${questionIndex + 1}`} onClick={onDuplicate}>
                        <ContentCopyOutlined fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Remove question">
                    <span>
                        <IconButton
                            aria-label={`Remove question ${questionIndex + 1}`}
                            onClick={onDelete}
                            disabled={!canDelete}
                        >
                            <DeleteOutline fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                        <InputLabel id={`${path}-type-label`}>Answer type</InputLabel>
                        <Select
                            labelId={`${path}-type-label`}
                            label="Answer type"
                            value={question.type}
                            onChange={(e) =>
                                setFieldValue(path, applyTypeChange(question, e.target.value as QuestionType))
                            }
                        >
                            {(Object.keys(TYPE_LABELS) as QuestionType[]).map((t) => (
                                <MenuItem key={t} value={t}>
                                    {TYPE_LABELS[t]}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={8}>
                    <Box display="flex" flexWrap="wrap" gap={2}>
                        {showReverse ? (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={question.reverseScored}
                                        onChange={(e) => setFieldValue(`${path}.reverseScored`, e.target.checked)}
                                    />
                                }
                                label="Reverse-scored"
                            />
                        ) : null}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={question.scored}
                                    disabled={question.type === "freeText"}
                                    onChange={(e) => setFieldValue(`${path}.scored`, e.target.checked)}
                                />
                            }
                            label="Counts toward score"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={question.required}
                                    onChange={(e) => setFieldValue(`${path}.required`, e.target.checked)}
                                />
                            }
                            label="Required"
                        />
                    </Box>
                </Grid>
            </Grid>

            {/* Type-specific config */}
            <TypeConfig path={path} question={question} inheritsScale={inheritsScale} inheritedCount={inheritedCount} />
        </Paper>
    );
};

// --- per-type config editors ----------------------------------------------
const TypeConfig = ({
    path,
    question,
    inheritsScale,
    inheritedCount,
}: {
    path: string;
    question: SurveyQuestion;
    inheritsScale: boolean;
    inheritedCount: number;
}): JSX.Element | null => {
    const { handleChange, handleBlur, setFieldValue } = useFormikContext<SurveyDefinition>();
    const fieldError = useFieldError();

    if (question.type === "multi") {
        return (
            <Box mt={2}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" mb={1}>
                    Choices
                </Typography>
                <OptionsEditor name={`${path}.options`} minOptions={2} addLabel="Add choice" />
            </Box>
        );
    }

    if (question.type === "likert" || question.type === "single") {
        return (
            <Box mt={2}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="caption" color="textSecondary">
                    Uses the survey&apos;s shared scale ({inheritedCount} points).
                    {inheritsScale ? " Edit the shared scale above to change the options." : ""}
                </Typography>
            </Box>
        );
    }

    if (question.type === "vas") {
        return (
            <Box mt={2}>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            name={`${path}.vas.min`}
                            label="Min"
                            value={question.vas?.min ?? 0}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={Boolean(fieldError(`${path}.vas.min`))}
                            helperText={fieldError(`${path}.vas.min`)}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            name={`${path}.vas.max`}
                            label="Max"
                            value={question.vas?.max ?? 100}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={Boolean(fieldError(`${path}.vas.max`))}
                            helperText={fieldError(`${path}.vas.max`)}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            size="small"
                            name={`${path}.vas.minLabel`}
                            label="Min label"
                            value={question.vas?.minLabel ?? ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            size="small"
                            name={`${path}.vas.maxLabel`}
                            label="Max label"
                            value={question.vas?.maxLabel ?? ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    </Grid>
                </Grid>
            </Box>
        );
    }

    if (question.type === "freeText") {
        return (
            <Box mt={2}>
                <Divider sx={{ mb: 2 }} />
                <FormControlLabel
                    control={
                        <Switch
                            checked={question.freeText?.multiline ?? true}
                            onChange={(e) =>
                                setFieldValue(`${path}.freeText`, {
                                    ...question.freeText,
                                    multiline: e.target.checked,
                                })
                            }
                        />
                    }
                    label="Multi-line answer"
                />
            </Box>
        );
    }

    if (question.type === "nps") {
        return (
            <Box mt={2}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="caption" color="textSecondary">
                    Fixed 0–10 net-promoter scale.
                </Typography>
            </Box>
        );
    }

    return null;
};
